import Papa from "papaparse";
import { read, utils } from "xlsx";
import { IPepWatchFrequency, parsedFieldData } from "../types/PepTypes";
/**
 * Extracts the date of birth from a valid Icelandic kennitala (SSN)
 * @param kennitala - Valid Icelandic kennitala in format 'DDMMYY-XXXX' or 'DDMMYYXXXX'
 * @returns String in format 'YYYY-MM-DD' representing the date of birth
 */
export function extractDOBFromSSN(kennitala: string): string {
  // Remove any whitespace and hyphen
  const cleanKennitala = kennitala.replace(/[-\s]/g, '');
  
  // Extract date parts
  const day = cleanKennitala.substring(0, 2);
  const month = cleanKennitala.substring(2, 4);
  const year = cleanKennitala.substring(4, 6);
  const centuryDigit = cleanKennitala.charAt(9);
  
  // Determine full year based on century digit
  const fullYear = centuryDigit === '9' ? `19${year}` : `20${year}`;
  
  return `${fullYear}-${month}-${day}`;
}

export function formatDateStringToYYYYMMDD(dateString: string | undefined): string | null {
  try {
    if (!dateString) {
      return null; // Handle undefined or null input
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null; // Invalid date
    }
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error("Error formatting date string:", error);
    return null;
  }
}

interface ParsedName {
  firstName: string;
  middleName?: string;
  lastName: string;
}
/**
 * Parses a full name string into its components: first name, middle name(s), and last name.
 * @param fullName - The full name string to parse.
 * @returns An object containing the first name, middle name(s) (if any), and last name.
 * @throws Error if the full name does not contain at least a first and last name.
 */
export function parseFullName(fullName: string): ParsedName {
  // Split the full name into an array of name parts and remove any extra spaces
  const nameParts = fullName
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);

  // Need at least 2 parts for first and last name
  if (nameParts.length < 2) {
    throw new Error("Full name must include at least first and last name");
  }

  // The first element is the first name
  const firstName = nameParts[0];

  // The last element is the last name
  const lastName = nameParts[nameParts.length - 1];

  // If there are middle parts, join them together
  const middleParts = nameParts.slice(1, nameParts.length - 1);
  const middleName = middleParts.length > 0 ? middleParts.join(" ") : undefined;

  return {
    firstName,
    ...(middleName && { middleName }), // Only include middleName if it exists
    lastName,
  };
}

export function formatFullName(personData: { firstName: string; middleName?: string; lastName: string }): string {
  // Create an array of the name parts that exist
  const nameParts = [
    personData.firstName,
    ...(personData.middleName ? [personData.middleName] : []),
    personData.lastName,
  ];

  // Join the array with spaces
  return nameParts.join(" ");
}

/**
 * Parse CSV file using PapaParse with position-based column mapping
 * @param file The CSV file to parse
 * @returns Promise that resolves with standardized parsed data
 */
export const parseCSV = (file: File): Promise<parsedFieldData[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: false, // Changed to false to get array-based data
      skipEmptyLines: true,
      dynamicTyping: false, // Keep as strings for consistency
      delimitersToGuess: [",", ";", "\t"],
      complete: (results) => {
        if (results.errors.length > 0) {
          console.warn("CSV parsing warnings:", results.errors);
        }

        if (results.data && results.data.length > 0) {
          // Skip header row and standardize the output format
          const dataRows = results.data.slice(1) as string[][];
          const normalizedData = normalizeByPosition(dataRows);
          resolve(normalizedData);
        } else {
          reject(new Error("CSV file appears to be empty or improperly formatted"));
        }
      },
      error: (error) => {
        reject(new Error(`Failed to parse CSV: ${error.message}`));
      },
    });
  });
};

/**
 * Parse Excel file using SheetJS with position-based column mapping
 * @param file The Excel file to parse
 * @returns Promise that resolves with standardized parsed data
 */
export const parseExcel = (file: File): Promise<parsedFieldData[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) {
          reject(new Error("Failed to read file content"));
          return;
        }
        
        const workbook = read(data, {
          type: "array",
          cellDates: false, // Keep dates as strings
          cellStyles: true,
          cellNF: true,
          cellFormula: true,
          sheetStubs: true,
        });
        
        if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
          reject(new Error("Excel file contains no sheets"));
          return;
        }
        
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        if (!worksheet) {
          reject(new Error("Failed to access worksheet"));
          return;
        }
        
        // Convert to array of arrays instead of JSON to preserve column positions
        const arrayData = utils.sheet_to_json(worksheet, {
          header: 1, // This gives us array of arrays
          defval: null,
          raw: false,
        }) as any[][];
        
        // Skip header row and normalize by position
        const dataRows = arrayData.slice(1);
        const normalizedData = normalizeByPosition(dataRows);
        resolve(normalizedData);
      } catch (error) {
        reject(new Error(`Failed to parse Excel file: ${error instanceof Error ? error.message : "Unknown error"}`));
      }
    };
    
    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };
    
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Alternative async/await version for Excel parsing
 */
export const parseExcelAsync = async (file: File): Promise<parsedFieldData[]> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    
    const workbook = read(arrayBuffer, {
      type: "array",
      cellDates: false, // Keep dates as strings
      cellStyles: true,
      cellNF: true,
      cellFormula: true,
      sheetStubs: true,
    });
    
    if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
      throw new Error("Excel file contains no sheets");
    }
    
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    
    if (!worksheet) {
      throw new Error("Failed to access worksheet");
    }
    
    // Convert to array of arrays to preserve column positions
    const arrayData = utils.sheet_to_json(worksheet, {
      header: 1, // This gives us array of arrays
      defval: null,
      raw: false,
    }) as any[][];
    
    // Skip header row and normalize by position
    const dataRows = arrayData.slice(1);
    return normalizeByPosition(dataRows);
  } catch (error) {
    throw new Error(`Failed to parse Excel file: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
};

/**
 * Normalize parsed data by column position to standardized format
 * Maps columns by position: [PersonId, Name, CountryCode, DateOfBirth, Gender]
 * @param data Array of arrays representing rows and columns
 * @returns Standardized data with consistent column names, filtering out empty rows
 */
export const normalizeByPosition = (data: any[][]): parsedFieldData[] => {
  return data
    .filter(row => {
      // Filter out rows that don't exist or have no length
      if (!row || row.length === 0) {
        return false;
      }
      
      // Check if the row has any meaningful data in the first 5 columns
      // (PersonId, Name, CountryCode, DateOfBirth, Gender)
      const hasData = row.slice(0, 5).some(cell => {
        if (cell === null || cell === undefined) return false;
        const stringValue = String(cell).trim();
        return stringValue !== "";
      });
      
      return hasData;
    })
    .map((row): parsedFieldData => {
      return {
        PersonId: normalizeValue(row[0]),
        Name: normalizeValue(row[1]),
        CountryCode: normalizeValue(row[2]),
        DateOfBirth: normalizeValue(row[3]),
        Gender: normalizeValue(row[4]),
        Reference: normalizeValue(row[5]) || "", // Ensure Reference is always a string
      };
    });
};

/**
 * Normalize individual cell values
 * @param value The raw cell value
 * @returns Normalized value (string or undefined)
 */
const normalizeValue = (value: any): string | undefined => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return undefined;
  }
  
  // Convert to string and trim
  const stringValue = String(value).trim();
  
  // Return undefined for empty strings
  return stringValue === "" ? undefined : stringValue;
};


/**
 * Utility function to detect file type and parse accordingly
 * @param file The file to parse
 * @returns Promise that resolves with standardized parsed data
 */
export const parseFile = async (file: File): Promise<parsedFieldData[]> => {
  const fileName = file.name.toLowerCase();
  
  if (fileName.endsWith('.csv')) {
    return parseCSV(file);
  } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
    return parseExcelAsync(file);
  } else {
    throw new Error(`Unsupported file type: ${file.name}`);
  }
};

/**
 * Legacy function for backward compatibility
 * @deprecated Use normalizeByPosition instead
 */
export const normalizeParseResult = (data: any[]): any[] => {
  console.warn('normalizeParseResult is deprecated. Use normalizeByPosition for position-based parsing.');
  return data.map((row) => {
    const normalizedRow: Record<string, any> = {};

    Object.entries(row).forEach(([key, value]) => {
      if (value === "") {
        normalizedRow[key] = null;
      } else if (typeof value === "number") {
        normalizedRow[key] = String(value);
      } else if (value === null) {
        normalizedRow[key] = null;
      } else if (typeof value === "string") {
        const trimmed = value.trim();
        normalizedRow[key] = trimmed === "" ? null : trimmed;
      } else {
        normalizedRow[key] = value;
      }
    });

    return normalizedRow;
  });
};

export const mapToTableEntries = (parsedData: parsedFieldData[]): (parsedFieldData & IPepWatchFrequency)[] => {
  return parsedData
    .filter(row => {
      // Additional filtering at this level to ensure we don't process completely empty rows
      const { PersonId, Name, DateOfBirth, Gender, CountryCode, Reference } = row;
      return PersonId || Name || DateOfBirth || Gender || CountryCode || Reference;
    })
    .map(({ PersonId, Name, DateOfBirth, Gender, CountryCode, Reference }) => {
      let PepFrequency = "never"; // Default value for PEP frequency
      let SanctionsFrequency = "never"; // Default value for Sanctions frequency
      
      if (PersonId !== undefined) {
        PepFrequency = "daily"; // Example default frequency for PEP
      }
      
      if (Name && DateOfBirth && CountryCode) {
        SanctionsFrequency = "daily"; // Example default frequency for Sanctions
      }
      
      return {
        PersonId: PersonId,
        PepFrequency: PepFrequency,
        SanctionsFrequency: SanctionsFrequency,
        Name: Name,
        DateOfBirth: DateOfBirth,
        Gender: Gender,
        CountryCode: CountryCode,
        Reference: Reference || "", // Ensure Reference is always a string
      };
    });
};

export const handleBulkAddBody = (parsedData: parsedFieldData[]) => {
  return parsedData
    .filter(row => {
      // Filter out rows that have no meaningful data
      const { PersonId, Name, DateOfBirth, CountryCode, Gender, Reference } = row;
      return PersonId || Name || DateOfBirth || CountryCode || Gender || Reference;
    })
    .map(({ PersonId, Name, DateOfBirth, CountryCode, Gender, Reference }) => {
      let firstName, middleName, lastName = "";
      let body = {};
      
      // Check for personID
      if (!!PersonId) {
        body = {
          ...body,
          personId: PersonId,
          pepFrequency: "daily", // Default frequency for PEP
        };
      }
      
      // If the name is not empty, parse it
      if (!!Name) {
        const nameParts = parseFullName(Name);
        firstName = nameParts.firstName;
        middleName = nameParts.middleName;
        lastName = nameParts.lastName;
        
        // Add the parsed name to the body
        // firstname and lastname are required
        body = {
          ...body,
          personFirstName: firstName,
          personLastName: lastName,
        };
        
        // Only add middleName if it exists
        if (middleName) {
          body = {
            ...body,
            personMiddleName: middleName,
          };
        }
      }

      // If the date of birth is not empty, parse it
      if (!!DateOfBirth) {
        body = {
          ...body,
          personDateOfBirth: DateOfBirth,
        };
      }

      // If the country code is not empty, parse it
      if (!!CountryCode) {
        body = {
          ...body,
          personCountryCode: CountryCode,
        };
      }
      
      // If the Gender is not empty, parse it
      if (!!Gender) {
        body = {
          ...body,
          personGender: Gender,
        };
      }

      if (!!DateOfBirth && !!CountryCode && !!Name) {
        body = {
          ...body,
          sanctionsFrequency: "daily", // Default frequency for Sanctions
        };
      }

      // if the reference is not empty, add it to the body
      if (Reference) {
        body = {
          ...body,
          Reference: Reference,
        };
      }
      
      return body;
    });
};